import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    BookOpen,
    ChevronRight,
    Compass,
    Languages,
    Mic,
    PenTool,
    Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type { TopicDisplaySession } from "@/components/topic/TopicCard";
import { getCategoryMeta } from "@/lib/categories";
import { slateCardClass, slateSurfaceClass } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { formatRelativeShort } from "@/lib/time";
import { typeMetaClass, typeTitleClass } from "@/lib/typography";

export type ContinueActivityKind = "practised" | "studied" | "created";

interface ContinueRow extends TopicDisplaySession {
    /** When the learner last engaged with it, for the trailing meta. */
    touchedAt?: string;
    /** How the learner engaged with this topic most recently. */
    activityKind?: ContinueActivityKind;
}

interface ContinuePracticingProps {
    sessions: ContinueRow[];
    loading?: boolean;
    /** Shown when there is nothing to continue. */
    onBrowse: () => void;
    className?: string;
}

const countOf = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

const ACTIVITY_META: Record<
    ContinueActivityKind,
    {
        icon: typeof Mic;
        key: "practised" | "studied" | "created";
        fallback: string;
        chipClass: string;
    }
> = {
    practised: {
        icon: Mic,
        key: "practised",
        fallback: "You practised",
        chipClass:
            "bg-primary/10 text-primary border-primary/20 dark:bg-primary/15 dark:text-blue-300 dark:border-primary/30",
    },
    studied: {
        icon: BookOpen,
        key: "studied",
        fallback: "You studied",
        chipClass:
            "bg-amber-500/12 text-amber-800 border-amber-500/25 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30",
    },
    created: {
        icon: PenTool,
        key: "created",
        fallback: "You created",
        chipClass:
            "bg-emerald-500/12 text-emerald-800 border-emerald-500/25 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30",
    },
};

/**
 * "Where you left off" — the learner's active study desk.
 *
 * The most recent topic is promoted as a visual **lead lesson card** with its
 * category banner thumbnail, serif Fraunces title, activity status chip
 * ("You practised" / "You studied" / "You created"), and direct **Practise**
 * and **View** actions so the learner can jump straight back into speaking
 * without an extra page load.
 *
 * Remaining topics (up to 4) sit in a crisp notebook-style ledger directly
 * beneath, preserving fast vertical scanning and full-width titles.
 */
const ContinuePracticing = ({
    sessions,
    loading = false,
    onBrowse,
    className,
}: ContinuePracticingProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const rows = useMemo(() => sessions.slice(0, 5), [sessions]);
    const lead = rows[0] ?? null;
    const rest = useMemo(() => rows.slice(1), [rows]);

    const openView = useCallback(
        (row: ContinueRow) => navigate(`/session/${row.slug ?? row.id}`),
        [navigate],
    );

    const openPractice = useCallback(
        (row: ContinueRow) => navigate(`/session/${row.slug ?? row.id}/practice`),
        [navigate],
    );

    const heading = (
        <div className="flex items-baseline justify-between gap-3">
            <h2 className={cn(typeTitleClass, "inline-flex items-center gap-2")}>
                <BookOpen className="h-4 w-4 text-primary" aria-hidden="true" />
                {t("pages.topics.pickUpTitle", "Pick up where you left off")}
            </h2>
            {rows.length > 0 && (
                <button
                    type="button"
                    onClick={onBrowse}
                    className="inline-flex items-center gap-1 text-xs font-medium text-stone-500 transition-colors hover:text-primary dark:text-stone-400"
                >
                    {t("home.continue.browse", "Browse topics")}
                    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
            )}
        </div>
    );

    if (loading) {
        return (
            <section
                aria-label="Continue learning"
                className={cn("space-y-3", className)}
            >
                {heading}
                <div className="space-y-2.5" aria-hidden="true">
                    <div className={cn(slateCardClass, "h-[108px] animate-pulse")} />
                    {[0, 1].map((i) => (
                        <div
                            key={i}
                            className={cn(slateCardClass, "h-[64px] animate-pulse")}
                        />
                    ))}
                </div>
            </section>
        );
    }

    if (!lead) {
        return (
            <section
                aria-label="Continue learning"
                className={cn("space-y-3", className)}
            >
                {heading}
                <div
                    className={cn(
                        slateCardClass,
                        "relative overflow-hidden px-4 py-7 text-center sm:px-6 sm:py-9",
                        "bg-gradient-to-br from-stone-50/80 via-background to-amber-50/30 dark:from-stone-900/50 dark:via-background dark:to-amber-950/10",
                    )}
                >
                    <span className="mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <Compass className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <p className="font-display text-lg font-semibold text-stone-900 dark:text-stone-50">
                        {t("home.continue.empty", "Nothing in progress yet")}
                    </p>
                    <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-stone-500 sm:text-sm dark:text-stone-400">
                        {t(
                            "home.continue.emptyDescription",
                            "Pick a topic and your work will show up here.",
                        )}
                    </p>
                    <button
                        type="button"
                        onClick={onBrowse}
                        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-strong px-4 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
                    >
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("home.continue.browse", "Browse topics")}
                    </button>
                </div>
            </section>
        );
    }

    const leadCategory = lead.category || "General";
    const leadMeta = getCategoryMeta(leadCategory);
    const LeadCategoryIcon = leadMeta.icon;
    const leadBanner = lead.banner_url || leadMeta.bannerUrl;
    const leadQ = countOf(lead.questions);
    const leadV = countOf(lead.vocabulary);
    const leadKind: ContinueActivityKind = lead.activityKind ?? "practised";
    const leadActivity = ACTIVITY_META[leadKind];
    const LeadActivityIcon = leadActivity.icon;

    return (
        <section
            aria-label="Continue learning"
            data-tour="practice-widget"
            className={cn("space-y-3", className)}
        >
            {heading}

            <div className="space-y-2.5">
                {/* Lead lesson card — visual desk bookmark with category artwork,
                    Fraunces subject title, and direct Practise / View controls. */}
                <article
                    className={cn(
                        slateCardClass,
                        "group relative overflow-hidden",
                        "bg-[linear-gradient(125deg,hsl(38_55%_98%)_0%,hsl(var(--background))_50%,hsl(214_75%_97.5%)_100%)]",
                        "dark:bg-[linear-gradient(125deg,hsl(28_14%_13%)_0%,hsl(var(--background))_50%,hsl(220_25%_13%)_100%)]",
                        "transition-all duration-200 hover:border-primary/40 hover:shadow-md",
                    )}
                >
                    <span
                        aria-hidden="true"
                        className="absolute inset-y-0 left-0 w-1 bg-primary"
                    />

                    <div className="flex flex-col gap-3.5 p-3.5 pl-4 sm:flex-row sm:items-center sm:gap-4 sm:p-4 sm:pl-5">
                        {/* Visual lesson thumbnail */}
                        <button
                            type="button"
                            onClick={() => openView(lead)}
                            tabIndex={-1}
                            aria-hidden="true"
                            className="relative h-28 w-full shrink-0 overflow-hidden rounded-xl border border-stone-200/70 bg-stone-100 text-left sm:h-20 sm:w-32 dark:border-stone-800 dark:bg-stone-800"
                        >
                            <img
                                src={leadBanner}
                                alt=""
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-950/55 via-stone-950/10 to-transparent" />
                            <span
                                className={cn(
                                    "absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5",
                                    "border border-white/20 bg-stone-900/75 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur-sm",
                                )}
                            >
                                <LeadCategoryIcon className="h-2.5 w-2.5 text-amber-300" aria-hidden="true" />
                                <span className="max-w-[6.5rem] truncate">
                                    {t(`pages.topics.categories.${leadCategory}`, {
                                        defaultValue: leadCategory,
                                    })}
                                </span>
                            </span>
                        </button>

                        {/* Lesson subject & educational metadata */}
                        <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                                <span
                                    className={cn(
                                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                                        leadActivity.chipClass,
                                    )}
                                >
                                    <LeadActivityIcon className="h-2.5 w-2.5" aria-hidden="true" />
                                    {t(`home.continue.${leadActivity.key}`, leadActivity.fallback)}
                                </span>
                                {lead.touchedAt && (
                                    <span className={typeMetaClass}>
                                        {formatRelativeShort(lead.touchedAt)}
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={() => openView(lead)}
                                className="block max-w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                            >
                                <h3 className="font-display text-lg font-semibold leading-snug tracking-tight text-stone-900 transition-colors group-hover:text-primary sm:text-xl dark:text-stone-50">
                                    {lead.topic}
                                </h3>
                            </button>

                            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500 dark:text-stone-400">
                                {leadQ > 0 && (
                                    <span className="inline-flex items-center gap-1">
                                        <BookOpen className="h-3 w-3 text-primary/80" aria-hidden="true" />
                                        {t("common.count.question", {
                                            count: leadQ,
                                            defaultValue:
                                                leadQ === 1
                                                    ? "{{count}} question"
                                                    : "{{count}} questions",
                                        })}
                                    </span>
                                )}
                                {leadV > 0 && (
                                    <span className="inline-flex items-center gap-1">
                                        <Languages className="h-3 w-3 text-amber-600 dark:text-amber-400" aria-hidden="true" />
                                        {t("common.count.vocab", {
                                            count: leadV,
                                            defaultValue: "{{count}} vocab",
                                        })}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Actions: direct speaking practice or study view */}
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                type="button"
                                onClick={() => openPractice(lead)}
                                className={cn(
                                    "inline-flex h-9 items-center gap-1.5 rounded-full bg-primary-strong px-4 text-xs font-semibold text-primary-foreground shadow-sm",
                                    "transition-all hover:bg-primary/90 active:scale-[0.98]",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                )}
                            >
                                <Mic className="h-3.5 w-3.5" aria-hidden="true" />
                                {t("common.actions.resume", "Resume")}
                            </button>
                            <button
                                type="button"
                                onClick={() => openView(lead)}
                                className={cn(
                                    "inline-flex h-9 items-center gap-1 rounded-full border border-stone-200 bg-white/90 px-3 text-xs font-medium text-stone-700",
                                    "transition-colors hover:border-primary/40 hover:text-primary",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                    "dark:border-stone-800 dark:bg-stone-900 dark:text-stone-200",
                                )}
                            >
                                {t("home.topicOfDay.view", "View")}
                                <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </article>

                {/* Remaining recent topics in a compact study ledger */}
                {rest.length > 0 && (
                    <ul
                        className={cn(
                            slateCardClass,
                            "divide-y divide-stone-200/70 dark:divide-stone-800",
                        )}
                    >
                        {rest.map((row) => {
                            const category = row.category || "General";
                            const {
                                icon: CategoryIcon,
                                iconColor,
                                bannerUrl: catBanner,
                            } = getCategoryMeta(category);
                            const thumb = row.banner_url || catBanner;
                            const qCount = countOf(row.questions);
                            const vCount = countOf(row.vocabulary);
                            const kind: ContinueActivityKind =
                                row.activityKind ?? "practised";
                            const activity = ACTIVITY_META[kind];

                            return (
                                <li key={row.id}>
                                    <button
                                        type="button"
                                        onClick={() => openView(row)}
                                        className="group relative flex w-full items-center gap-3.5 px-4 py-3 text-left transition-colors hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:hover:bg-stone-800/40"
                                    >
                                        <span
                                            aria-hidden
                                            className="absolute inset-y-0 left-0 w-1 origin-center scale-y-0 rounded-r-full bg-primary/70 transition-transform duration-200 group-hover:scale-y-100 dark:bg-primary/60"
                                        />

                                        {/* Visual category thumbnail */}
                                        <span
                                            className={cn(
                                                "relative inline-flex h-11 w-14 shrink-0 overflow-hidden rounded-lg border border-stone-200/70 dark:border-stone-800",
                                                "transition-transform duration-200 group-hover:scale-105",
                                                slateSurfaceClass,
                                                "!p-0",
                                            )}
                                            aria-hidden="true"
                                        >
                                            <img
                                                src={thumb}
                                                alt=""
                                                loading="lazy"
                                                className="h-full w-full object-cover"
                                            />
                                            <span className="absolute inset-0 bg-stone-950/20" />
                                            <span className="absolute bottom-1 right-1 flex h-4 w-4 items-center justify-center rounded bg-white/90 shadow-sm dark:bg-stone-900/90">
                                                <CategoryIcon
                                                    className={cn("h-2.5 w-2.5", iconColor)}
                                                />
                                            </span>
                                        </span>

                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[15px] font-semibold leading-snug tracking-tight text-stone-900 group-hover:text-primary dark:text-stone-50">
                                                {row.topic}
                                            </span>
                                            <span className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-stone-500 dark:text-stone-400">
                                                <span className="font-medium text-stone-600 dark:text-stone-300">
                                                    {t(
                                                        `home.continue.${activity.key}`,
                                                        activity.fallback,
                                                    )}
                                                </span>
                                                {qCount > 0 && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <BookOpen
                                                            className="h-3 w-3"
                                                            aria-hidden="true"
                                                        />
                                                        {t("common.count.question", {
                                                            count: qCount,
                                                            defaultValue:
                                                                qCount === 1
                                                                    ? "{{count}} question"
                                                                    : "{{count}} questions",
                                                        })}
                                                    </span>
                                                )}
                                                {vCount > 0 && (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Languages
                                                            className="h-3 w-3"
                                                            aria-hidden="true"
                                                        />
                                                        {t("common.count.vocab", {
                                                            count: vCount,
                                                            defaultValue: "{{count}} vocab",
                                                        })}
                                                    </span>
                                                )}
                                                {row.touchedAt && (
                                                    <span className={typeMetaClass}>
                                                        {formatRelativeShort(row.touchedAt)}
                                                    </span>
                                                )}
                                            </span>
                                        </span>

                                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium text-stone-500 transition-colors group-hover:border-primary/25 group-hover:bg-primary/10 group-hover:text-primary dark:text-stone-400 dark:group-hover:border-primary/30 dark:group-hover:bg-primary/15 dark:group-hover:text-primary">
                                            {t("common.actions.resume", "Resume")}
                                            <ChevronRight
                                                className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
                                                aria-hidden="true"
                                            />
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
};

export { ContinuePracticing };
export type { ContinuePracticingProps, ContinueRow };
